/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonFilterERP,
  ButtonPrimeReact,
  CalendarPrimeReact,
  InputCurrency,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DocumentoComercialValidacionDtoQueryParamsProps } from "api/interfaces/DocumentoComercialValidacionDtoQueryParamsProps"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { backendDateFormat, frontendDateFormat } from "utils/formatDate"

const FilterMenu = ({ serviceQuery }: { serviceQuery: $TSMigrationFixMe }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [keyword, setKeyword] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)
  const [amount, setAmount] = useState<{
    min: number | undefined
    max: number | undefined
  }>({
    min: undefined,
    max: undefined,
  })
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (
    queryParams: DocumentoComercialValidacionDtoQueryParamsProps
  ) => {
    const documentosParams = {
      FechaEmisionLowerLimit: queryParams.FechaEmisionLowerLimit,
      FechaEmisionUpperLimit: queryParams.FechaEmisionUpperLimit,
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      RazonSocial: queryParams.RazonSocial,
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(documentosParams).filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
    )
    return fixedQueryParams
  }
  const handleQuery = () => {
    serviceQuery(
      queryFixer({
        FechaEmisionLowerLimit:
          date?.min && backendDateFormat(new Date(date.min)),
        FechaEmisionUpperLimit:
          date?.max && backendDateFormat(new Date(date.max)),
        ImporteLowerLimit: amount.min,
        ImporteUpperLimit: amount.max,
        RazonSocial: keyword,
      })
    )
  }
  const handleApplyFilters = () => {
    handleQuery()
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setAmount({ min: undefined, max: undefined })
    setDate({ min: undefined, max: undefined })
    setKeyword("")
  }

  const appliedFiltersTags = [
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min ? frontendCurrency(amount?.min) : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max ? frontendCurrency(amount?.max) : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },

    {
      label: `${t("filtros.appliedTag.dateFrom")}`,
      value: date?.min ? frontendDateFormat(new Date(date.min)) : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("filtros.appliedTag.dateTo")}`,
      value: date?.max ? frontendDateFormat(new Date(date.max)) : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters.length])

  return (
    <>
      <div className='tw-flex tw-flex-col sm:tw-flex-row tw-w-full xl:tw-w-fit tw-justify-center tw-gap-1 lg:tw-gap-3'>
        <ButtonFilterERP
          isActive={appliedFilters.length > 0}
          numberActive={appliedFilters.length}
          onClick={openFilters}
          deleteFilter={handleResetFilters}
          filterButtonLabel={`${t("filtros.title")}`}
        />
        <SearchInput
          placeholder={`${t("validaciones.placeholderSearch")}`}
          onChange={(ev: string) => {
            setKeyword(ev)
          }}
          inputValue={keyword}
          className='tw-h-10 tw-w-full sm:tw-w-72'
        />
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='filter-buttons position-bottom w-100 tw-border-t-2 tw-pt-6'>
              <p onClick={handleResetFilters}>{t("filtros.labelDelete")}</p>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary tw-cursor-pointer'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='flex tw- tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("filterMenu.appliedTag.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("filterMenu.appliedTag.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={`${filter.label}-${filter.value}`}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.importe.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMin'>
                  {t("filtros.importe.fromLabel")} (€)
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.min}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={0}
                  max={amount.max ?? Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, min: value })}
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMax'>
                  {t("filtros.importe.toLabel")} (€)
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.max}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, max: value })}
                />
              </div>
            </div>
          </div>

          <div className='tw-flex tw-flex-row tw-mb-6'>
            <div className='tw-flex tw-flex-col'>
              <label htmlFor='fechaDesde'>
                {t("filterMenu.invoice.issueDate.since")}
              </label>
              <CalendarPrimeReact
                id='fechaDesde'
                name='fechaDesde'
                clearButtonClassName='!tw-hidden'
                value={date?.min ? new Date(date.min) : undefined}
                locale={language}
                onChange={(e) => {
                  const eventDate = e.target.value as unknown as string
                  setDate({
                    max:
                      date?.max && new Date(eventDate) > new Date(date?.max)
                        ? undefined
                        : date?.max,
                    min: eventDate,
                  })
                }}
                placeholder='dd/mm/aaaa'
                dateFormat='dd/mm/yy'
                className='tw-w-full'
                baseZIndex={2302}
                showIcon
                maxDate={date?.max ? new Date(date.max) : undefined}
              />
            </div>
            <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
              -
            </div>
            <div className='tw-flex tw-flex-col'>
              <label htmlFor='fechaHasta'>
                {t("filterMenu.invoice.issueDate.to")}
              </label>
              <CalendarPrimeReact
                id='fechaHasta'
                name='fechaHasta'
                clearButtonClassName='!tw-hidden'
                value={date?.max ? new Date(date.max) : undefined}
                locale={language}
                onChange={(e) => {
                  const eventDate = e.target.value as unknown as string
                  setDate({
                    min:
                      date?.min && new Date(eventDate) < new Date(date?.min)
                        ? undefined
                        : date?.min,
                    max: eventDate,
                  })
                }}
                placeholder='dd/mm/aaaa'
                dateFormat='dd/mm/yy'
                className='tw-w-full'
                baseZIndex={2302}
                showIcon
                minDate={date?.min ? new Date(date.min) : undefined}
              />
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}

export default FilterMenu
